import type { PromptSegment } from './prompt-segment';

// Parses the prompterText HTML emitted by filters-store.generatePrompterText
// into structured segments. Replaces nav-bar-logic.transformPrompterTextToHtml:
// instead of rewriting HTML for innerHTML injection, we produce data that
// <Prompt> renders as JSX. Pure (uses DOMParser like htmlToPlainText). The
// decision tree mirrors the original transform exactly.
export function parsePrompterHtml(html: string): PromptSegment[] {
  const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
  const segments: PromptSegment[] = [];

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text) segments.push({ kind: 'text', text }); // keep whitespace (spacing)
      return;
    }
    if (!(node instanceof HTMLElement) || node.tagName !== 'STRONG') {
      const text = node.textContent ?? '';
      if (text) segments.push({ kind: 'text', text });
      return;
    }

    const el = node;
    const label = el.textContent ?? '';

    // Attribute-less <strong> = prefix words ("non", "not", "are not", …);
    // the original transform strips these to bare text.
    if (el.attributes.length === 0) {
      if (label) segments.push({ kind: 'text', text: label });
      return;
    }
    // Editable islands (transform returned these early as font-bold spans).
    if (el.hasAttribute('narrative') || el.id === 'narrative') {
      segments.push({ kind: 'editable', id: 'narrative', text: label });
      return;
    }
    if (el.hasAttribute('lyricscontains') || el.id === 'lyricscontains') {
      segments.push({ kind: 'editable', id: 'lyrics', text: label });
      return;
    }
    if (el.hasAttribute('artwork-id')) {
      segments.push({ kind: 'artwork', label, artworkId: el.getAttribute('artwork-id') ?? '' });
      return;
    }
    if (el.hasAttribute('artist-name')) {
      segments.push({ kind: 'artist', label, artistName: el.getAttribute('artist-name') ?? '' });
      return;
    }
    const stateAttr = el.getAttribute('filter-state');
    segments.push({
      kind: 'filter',
      label,
      familyName: el.getAttribute('filter-family-name') ?? '',
      state: stateAttr === 'green' || stateAttr === 'red' ? stateAttr : undefined,
    });
  });

  return segments;
}
